import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";

type Phase = "input" | "loading" | "success" | "error";

export default function JoinPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState<Phase>("input");
  const [errorMsg, setErrorMsg] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleJoin = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed || !user) return;
    setPhase("loading");
    setErrorMsg("");

    // invite_code로 workspace 조회
    const { data: ws, error } = await supabase
      .from("workspaces")
      .select("id, name, invite_code")
      .eq("invite_code", trimmed)
      .maybeSingle();

    if (error || !ws) {
      setPhase("error");
      setErrorMsg("유효하지 않은 초대 코드예요. 교수님께 코드를 다시 확인해주세요.");
      return;
    }

    // workspace_members에 등록
    const { error: memberError } = await supabase
      .from("workspace_members")
      .upsert(
        { workspace_id: ws.id, user_id: user.id, joined_at: new Date().toISOString() },
        { onConflict: "workspace_id,user_id" }
      );

    if (memberError) {
      setPhase("error");
      setErrorMsg("입장 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.");
      return;
    }

    // localStorage에 workspace 설정 (CompanyContext가 이걸 사용함)
    localStorage.setItem("kap_solo_workspace_id", ws.id);
    setWorkspaceName(ws.name);
    setPhase("success");
    setTimeout(() => navigate("/role-select"), 2500);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0C0C0E] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#4B6CF7]/30 border-t-[#4B6CF7] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* 로고 */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#4B6CF7]/15 border border-[#4B6CF7]/25 mx-auto mb-4">
            <i className="ri-building-2-line text-[#7A9BFF] text-2xl"></i>
          </div>
          <h1 className="text-[#F2F2F2] font-bold text-xl mb-1">수업 참여하기</h1>
          <p className="text-[#555555] text-sm">교수님이 공유한 초대 코드를 입력하세요</p>
        </div>

        <div className="bg-[#111112] border border-white/[0.07] rounded-2xl p-6">
          {phase === "success" ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-400/10 border border-emerald-400/20 mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-emerald-400 text-2xl"></i>
              </div>
              <h2 className="text-[#F2F2F2] font-bold text-base mb-1">입장 성공!</h2>
              <p className="text-[#888888] text-sm mb-2">
                <strong className="text-[#CCCCCC]">{workspaceName}</strong> 워크스페이스에 합류했어요
              </p>
              <p className="text-[#555555] text-xs">역할 선택 화면으로 이동 중...</p>
              <div className="mt-4 w-full bg-white/[0.04] rounded-full h-1">
                <div className="h-1 rounded-full bg-emerald-400/60 animate-pulse w-full"></div>
              </div>
            </div>
          ) : (
            <>
              {/* 안내 배너 */}
              <div className="flex items-start gap-3 bg-[#4B6CF7]/[0.07] border border-[#4B6CF7]/15 rounded-xl px-4 py-3 mb-5">
                <i className="ri-information-line text-[#7A9BFF] text-sm mt-0.5 shrink-0"></i>
                <p className="text-[#7A9BFF]/80 text-xs leading-relaxed">
                  초대 코드는 교수님의 관리자 콘솔 → 초대 &amp; 권한 관리 탭에서 확인할 수 있어요. 대소문자 구분 없이 입력하세요.
                </p>
              </div>

              {/* 코드 입력 */}
              <div className="mb-4">
                <label className="block text-[#888888] text-xs font-semibold uppercase tracking-widest mb-2">
                  초대 코드
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === "Enter" && handleJoin()}
                  placeholder="예: KAP001"
                  maxLength={20}
                  className="w-full bg-[#1C1F2E] border border-[#2A2D3E] focus:border-[#4B6CF7]/60 outline-none text-[#F2F2F2] text-lg font-mono font-bold px-4 py-3 rounded-xl placeholder-[#333344] transition-colors tracking-widest text-center"
                />
              </div>

              {phase === "error" && (
                <div className="flex items-start gap-2.5 bg-red-400/[0.07] border border-red-400/20 rounded-xl px-4 py-3 mb-4">
                  <i className="ri-error-warning-line text-red-400/80 text-sm mt-0.5 shrink-0"></i>
                  <p className="text-red-400/80 text-xs leading-relaxed">{errorMsg}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleJoin}
                disabled={!code.trim() || phase === "loading"}
                className="w-full py-3 bg-[#4B6CF7] hover:bg-[#5B7CF7] disabled:opacity-30 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                {phase === "loading" ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    코드 확인 중...
                  </>
                ) : (
                  <>
                    <i className="ri-login-circle-line"></i>
                    수업 입장하기
                  </>
                )}
              </button>

              <div className="mt-4 pt-4 border-t border-white/[0.05] text-center">
                <button
                  type="button"
                  onClick={() => navigate("/role-select")}
                  className="text-[#444444] text-xs hover:text-[#888888] cursor-pointer transition-colors"
                >
                  코드 없이 개인 실습으로 시작하기 →
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-[#333333] text-xs mt-5">
          같은 초대 코드를 쓰는 학생들끼리만 같은 공간에서 진행됩니다
        </p>
      </div>
    </div>
  );
}
